// Shared primitives for all CMS page editors
import { useState, useRef } from 'react'
import { cmsService } from '../../services/cmsService'

export function Field({ label, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-admin-muted uppercase tracking-wider">{label}</label>
            {children}
        </div>
    )
}

export function Input({ value, onChange, placeholder }) {
    return (
        <input
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40 transition-all"
        />
    )
}

export function Textarea({ value, onChange, rows = 3, placeholder, mono = false }) {
    return (
        <textarea
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            className={`w-full px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40 transition-all resize-y ${mono ? 'font-mono text-xs' : ''}`}
        />
    )
}

export function SectionCard({ title, description, onSave, saving, saved, error, children }) {
    return (
        <div className="bg-white border border-admin-border rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-admin-border bg-slate-50 flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-sm font-bold text-admin-heading">{title}</h3>
                    {description && <p className="text-xs text-admin-muted mt-0.5">{description}</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    {error && <span className="text-xs text-status-error font-medium">{error}</span>}
                    {saved && !error && (
                        <span className="text-xs text-status-success font-bold flex items-center gap-1">
                            <CheckIcon className="w-3.5 h-3.5" /> Saved
                        </span>
                    )}
                    <button
                        onClick={onSave}
                        disabled={saving}
                        className="px-4 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    >
                        {saving && <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />}
                        {saving ? 'Saving…' : 'Save Section'}
                    </button>
                </div>
            </div>
            <div className="p-6 space-y-5">{children}</div>
        </div>
    )
}

export function PageHeader({ title, description, liveUrl }) {
    return (
        <div className="flex items-start justify-between">
            <div>
                <h2 className="text-lg font-bold text-admin-heading">{title}</h2>
                <p className="text-sm text-admin-muted mt-1">{description}</p>
            </div>
            {liveUrl && (
                <a
                    href={liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-admin-muted border border-admin-border rounded hover:bg-slate-100 transition-all"
                >
                    <ExternalLinkIcon className="w-3.5 h-3.5" />
                    View Live Page
                </a>
            )}
        </div>
    )
}

export function VideoUpload({ value, onChange }) {
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState(null)
    const inputRef = useRef(null)

    const handleFile = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        setUploading(true)
        setError(null)
        try {
            const res = await cmsService.uploadCmsMedia(file)
            onChange(res.data.url)
        } catch (err) {
            setError(err.message || 'Upload failed')
        } finally {
            setUploading(false)
            e.target.value = ''
        }
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {uploading && <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />}
                    {uploading ? 'Uploading…' : 'Upload Video'}
                </button>
                <input ref={inputRef} type="file" accept="video/mp4,video/mov,video/webm,video/ogg,video/avi" onChange={handleFile} className="hidden" />
                {!value && !uploading && <span className="text-xs text-admin-muted">No video uploaded</span>}
            </div>
            {value && (
                <div className="p-3 bg-slate-50 border border-admin-border rounded-md space-y-1">
                    <p className="text-[10px] font-bold text-admin-muted uppercase tracking-wider">Current URL</p>
                    <p className="text-xs font-mono text-admin-text break-all">{value}</p>
                </div>
            )}
            {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
        </div>
    )
}

export function DocumentUpload({ value, onChange }) {
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState(null)
    const inputRef = useRef(null)

    const handleFile = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        setUploading(true)
        setError(null)
        try {
            const res = await cmsService.uploadCmsMedia(file)
            onChange(res.data.url)
        } catch (err) {
            setError(err.message || 'Upload failed')
        } finally {
            setUploading(false)
            e.target.value = ''
        }
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {uploading && <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />}
                    {uploading ? 'Uploading…' : 'Upload Document'}
                </button>
                <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" onChange={handleFile} className="hidden" />
                {!value && !uploading && <span className="text-xs text-admin-muted">No document uploaded</span>}
            </div>
            {value && (
                <div className="p-3 bg-slate-50 border border-admin-border rounded-md space-y-1">
                    <p className="text-[10px] font-bold text-admin-muted uppercase tracking-wider">Current URL</p>
                    <p className="text-xs font-mono text-admin-text break-all">{value}</p>
                </div>
            )}
            {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
        </div>
    )
}

export function ImageUpload({ value, onChange }) {
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState(null)
    const inputRef = useRef(null)

    const handleFile = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        setUploading(true)
        setError(null)
        try {
            const res = await cmsService.uploadCmsMedia(file)
            onChange(res.data.url)
        } catch (err) {
            setError(err.message || 'Upload failed')
        } finally {
            setUploading(false)
            e.target.value = ''
        }
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-3">
                {value && (
                    <div className="h-14 px-3 flex items-center bg-brand-navy rounded-md border border-admin-border">
                        <img src={value} alt="Logo preview" className="h-10 w-auto object-contain" />
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {uploading && <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />}
                    {uploading ? 'Uploading…' : value ? 'Replace Logo' : 'Upload Logo'}
                </button>
                <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp,.gif" onChange={handleFile} className="hidden" />
                {value && !uploading && (
                    <button type="button" onClick={() => onChange(null)} className="text-xs text-red-400 hover:text-red-600 font-bold">
                        Remove
                    </button>
                )}
                {!value && !uploading && <span className="text-xs text-admin-muted">No logo uploaded</span>}
            </div>
            {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
        </div>
    )
}

export function useSectionSave(page, cmsService) {
    // This is a hook factory — import and use in each page
}

export function CheckIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
    )
}

export function SpinnerIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
    )
}

export function ExternalLinkIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
    )
}
